import { z } from "zod";

export const SUPPORTED_LOCALES = ["fi", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const localeSchema = z.enum(SUPPORTED_LOCALES);

/**
 * Product-wide default when nothing else applies. Individual apps may pass
 * their own `defaultLocale` to `resolveLocale`/`createI18n` instead — the
 * clinic console defaults to "en", not this value.
 */
export const DEFAULT_LOCALE: Locale = "fi";

const LOCALE_OVERRIDE_KEY = "ferrivia.locale";

function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Reads and strips `?lang=` from the URL, persisting it as the localStorage
 * override. Must run at bootstrap, before React mounts:
 * `AuthCallback.tsx` calls `history.replaceState({}, "", "/")` after the eID
 * round trip, discarding any query params still on the URL at that point.
 */
export function readLangOverride(): Locale | undefined {
  if (typeof window === "undefined") return undefined;

  const url = new URL(window.location.href);
  const lang = url.searchParams.get("lang");
  if (!lang || !isSupportedLocale(lang)) return undefined;

  window.localStorage.setItem(LOCALE_OVERRIDE_KEY, lang);
  url.searchParams.delete("lang");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  return lang;
}

export function readStoredOverride(): Locale | undefined {
  if (typeof window === "undefined") return undefined;
  const stored = window.localStorage.getItem(LOCALE_OVERRIDE_KEY);
  return stored && isSupportedLocale(stored) ? stored : undefined;
}

export function hasStoredOverride(): boolean {
  return readStoredOverride() !== undefined;
}

export function persistLocaleOverride(locale: Locale): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCALE_OVERRIDE_KEY, locale);
}

function matchNavigatorLocale(): Locale | undefined {
  if (typeof navigator === "undefined") return undefined;
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of languages) {
    const prefix = lang?.slice(0, 2).toLowerCase();
    if (prefix && isSupportedLocale(prefix)) return prefix;
  }
  return undefined;
}

/**
 * Synchronous bootstrap resolution: `?lang=` override, then a stored
 * override, then a browser-language match, then `defaultLocale`. The
 * session-derived `users.locale` (step 3 in the plan) is applied later, once
 * a session exists, via `useSyncUserLocale`.
 */
export function resolveLocale(options: { defaultLocale?: Locale } = {}): Locale {
  const defaultLocale = options.defaultLocale ?? DEFAULT_LOCALE;
  return readLangOverride() ?? readStoredOverride() ?? matchNavigatorLocale() ?? defaultLocale;
}
